/**
 * HVN: Dashboard block — editor component.
 *
 * The preview is the real server render (ServerSideRender via BlockPreview),
 * which shows an informative panel in the editor (the live Workspace SPA only
 * boots on the frontend for signed-in users).
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	RangeControl,
	TextControl,
	TextareaControl,
} from '@wordpress/components';

const NAV_ITEMS = [
	{ id: 'dashboard', label: __( 'Dashboard', 'havenlytics' ) },
	{ id: 'properties', label: __( 'Properties', 'havenlytics' ) },
	{ id: 'saved-properties', label: __( 'Saved Properties', 'havenlytics' ) },
	{ id: 'inquiries', label: __( 'Inquiries', 'havenlytics' ) },
	{ id: 'profile', label: __( 'Profile', 'havenlytics' ) },
	{ id: 'analytics', label: __( 'Analytics', 'havenlytics' ) },
	{ id: 'notifications', label: __( 'Notifications', 'havenlytics' ) },
	{ id: 'settings', label: __( 'Settings', 'havenlytics' ) },
];

const STAT_CARDS = [
	__( 'Active Listings', 'havenlytics' ),
	__( 'New Inquiries', 'havenlytics' ),
	__( 'Pending Listings', 'havenlytics' ),
	__( 'Closed Volume', 'havenlytics' ),
];

/**
 * A skeleton card shell with a header and body content.
 *
 * @param {Object} props
 * @return {Element}
 */
function SkCard( { title, className, children } ) {
	return (
		<div className={ 'hvnly-dbsk__card' + ( className ? ' ' + className : '' ) }>
			<div className="hvnly-dbsk__card-head">
				<span className="hvnly-dbsk__t hvnly-dbsk__t--title">{ title }</span>
				<span className="hvnly-dbsk__b hvnly-dbsk__b--pill" />
			</div>
			<div className="hvnly-dbsk__card-body">{ children }</div>
		</div>
	);
}

/**
 * Lightweight, native dashboard skeleton for the editor.
 *
 * Purely presentational (no SPA, no iframe, no data). It mirrors the real
 * Workspace dashboard layout — sidebar, header, statistics, recent properties,
 * recent activity, quick actions — and honors the block's visibility toggles so
 * the editor preview resembles what the frontend will render.
 *
 * @param {Object} props
 * @param {Object} props.attributes Block attributes.
 * @return {Element}
 */
function DashboardSkeleton( { attributes } ) {
	const {
		showSidebar,
		showHeader,
		showWelcome,
		showStatistics,
		showRecentProperties,
		showActivity,
		showQuickActions,
		defaultSection,
	} = attributes;

	return (
		<div className="hvnly-dbsk" aria-hidden="true">
			<span className="hvnly-dbsk__chip">{ __( 'Preview', 'havenlytics' ) }</span>

			{ showSidebar && (
				<aside className="hvnly-dbsk__sidebar">
					<div className="hvnly-dbsk__brand">
						<span className="hvnly-dbsk__brand-dot" />
						<span className="hvnly-dbsk__t hvnly-dbsk__t--brand">
							{ __( 'Havenlytics', 'havenlytics' ) }
						</span>
					</div>
					<nav className="hvnly-dbsk__nav">
						{ NAV_ITEMS.map( ( item ) => (
							<span
								key={ item.id }
								className={
									'hvnly-dbsk__nav-item' +
									( item.id === defaultSection ? ' is-active' : '' )
								}
							>
								<span className="hvnly-dbsk__nav-ico" />
								<span className="hvnly-dbsk__nav-label">{ item.label }</span>
							</span>
						) ) }
					</nav>
				</aside>
			) }

			<div className="hvnly-dbsk__main">
				{ showHeader && (
					<header className="hvnly-dbsk__header">
						<span className="hvnly-dbsk__t hvnly-dbsk__t--pagetitle">
							{ __( 'Dashboard', 'havenlytics' ) }
						</span>
						<div className="hvnly-dbsk__header-right">
							<span className="hvnly-dbsk__search" />
							<span className="hvnly-dbsk__avatar" />
						</div>
					</header>
				) }

				<div className="hvnly-dbsk__content">
					{ showWelcome && (
						<div className="hvnly-dbsk__welcome">
							<span className="hvnly-dbsk__t hvnly-dbsk__t--lg" />
							<span className="hvnly-dbsk__b hvnly-dbsk__b--sub" />
						</div>
					) }

					{ showStatistics && (
						<div className="hvnly-dbsk__stats">
							{ STAT_CARDS.map( ( label ) => (
								<div key={ label } className="hvnly-dbsk__card hvnly-dbsk__stat">
									<span className="hvnly-dbsk__stat-ico" />
									<span className="hvnly-dbsk__t hvnly-dbsk__t--label">{ label }</span>
									<span className="hvnly-dbsk__b hvnly-dbsk__b--num" />
									<span className="hvnly-dbsk__b hvnly-dbsk__b--tiny" />
								</div>
							) ) }
						</div>
					) }

					<div className="hvnly-dbsk__cols">
						<div className="hvnly-dbsk__col-main">
							{ showActivity && (
								<SkCard title={ __( 'Recent Activity', 'havenlytics' ) }>
									{ [ 0, 1, 2, 3 ].map( ( i ) => (
										<div key={ i } className="hvnly-dbsk__activity-row">
											<span className="hvnly-dbsk__dot" />
											<span className="hvnly-dbsk__b hvnly-dbsk__b--line" />
											<span className="hvnly-dbsk__b hvnly-dbsk__b--time" />
										</div>
									) ) }
								</SkCard>
							) }

							{ showRecentProperties && (
								<SkCard title={ __( 'Recent Properties', 'havenlytics' ) }>
									{ [ 0, 1, 2 ].map( ( i ) => (
										<div key={ i } className="hvnly-dbsk__prop-row">
											<span className="hvnly-dbsk__thumb" />
											<span className="hvnly-dbsk__prop-lines">
												<span className="hvnly-dbsk__b hvnly-dbsk__b--line" />
												<span className="hvnly-dbsk__b hvnly-dbsk__b--half" />
											</span>
											<span className="hvnly-dbsk__b hvnly-dbsk__b--price" />
										</div>
									) ) }
								</SkCard>
							) }
						</div>

						{ showQuickActions && (
							<div className="hvnly-dbsk__col-side">
								<SkCard title={ __( 'Quick Actions', 'havenlytics' ) } className="hvnly-dbsk__quick">
									{ [
										__( 'Add Property', 'havenlytics' ),
										__( 'My Properties', 'havenlytics' ),
										__( 'Inquiries', 'havenlytics' ),
										__( 'Edit Profile', 'havenlytics' ),
									].map( ( label ) => (
										<span key={ label } className="hvnly-dbsk__action">
											<span className="hvnly-dbsk__action-ico" />
											{ label }
										</span>
									) ) }
								</SkCard>
							</div>
						) }
					</div>
				</div>
			</div>
		</div>
	);
}

const SECTION_OPTIONS = [
	{ label: __( 'Dashboard', 'havenlytics' ), value: 'dashboard' },
	{ label: __( 'Properties', 'havenlytics' ), value: 'properties' },
	{ label: __( 'Saved Properties', 'havenlytics' ), value: 'saved-properties' },
	{ label: __( 'Inquiries', 'havenlytics' ), value: 'inquiries' },
	{ label: __( 'Profile', 'havenlytics' ), value: 'profile' },
	{ label: __( 'Analytics', 'havenlytics' ), value: 'analytics' },
	{ label: __( 'Notifications', 'havenlytics' ), value: 'notifications' },
	{ label: __( 'Settings', 'havenlytics' ), value: 'settings' },
];

const LAYOUT_OPTIONS = [
	{ label: __( 'Default', 'havenlytics' ), value: 'default' },
	{ label: __( 'Contained', 'havenlytics' ), value: 'contained' },
	{ label: __( 'Full width', 'havenlytics' ), value: 'full' },
];

const SHADOW_OPTIONS = [
	{ label: __( 'Inherit', 'havenlytics' ), value: 'default' },
	{ label: __( 'None', 'havenlytics' ), value: 'none' },
	{ label: __( 'Small', 'havenlytics' ), value: 'sm' },
	{ label: __( 'Medium', 'havenlytics' ), value: 'md' },
	{ label: __( 'Large', 'havenlytics' ), value: 'lg' },
];

const CONTAINER_STYLE_OPTIONS = [
	{ label: __( 'Default', 'havenlytics' ), value: 'default' },
	{ label: __( 'Flat', 'havenlytics' ), value: 'flat' },
	{ label: __( 'Bordered', 'havenlytics' ), value: 'bordered' },
];

const SIDEBAR_OPTIONS = [
	{ label: __( 'Auto (app default)', 'havenlytics' ), value: 'auto' },
	{ label: __( 'Expanded', 'havenlytics' ), value: 'expanded' },
	{ label: __( 'Collapsed', 'havenlytics' ), value: 'collapsed' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		fullScreen,
		dashboardLayout,
		defaultSection,
		containerWidth,
		minHeight,
		showHeader,
		showSidebar,
		showWelcome,
		showStatistics,
		showQuickActions,
		showRecentProperties,
		showFavorites,
		showActivity,
		showProfileSummary,
		sidebarCollapse,
		cardRadius,
		spacing,
		shadow,
		containerStyle,
		loggedOutMode,
		loggedOutHeading,
		loggedOutMessage,
		authButtonLabel,
		authUrl,
	} = attributes;

	const blockProps = useBlockProps();

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'General', 'havenlytics' ) } initialOpen={ true }>
					<ToggleControl
						label={ __( 'Full screen (match Workspace)', 'havenlytics' ) }
						checked={ !! fullScreen }
						onChange={ ( value ) => setAttributes( { fullScreen: value } ) }
						help={ __( 'Renders the page through the existing Workspace canvas template — no theme header/footer, full-bleed and pixel-identical to the standalone Agent Workspace. Turn off to embed the dashboard inside the normal themed page.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Dashboard layout', 'havenlytics' ) }
						value={ dashboardLayout }
						options={ LAYOUT_OPTIONS }
						onChange={ ( value ) => setAttributes( { dashboardLayout: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Default section', 'havenlytics' ) }
						value={ defaultSection }
						options={ SECTION_OPTIONS }
						onChange={ ( value ) => setAttributes( { defaultSection: value } ) }
						help={ __( 'The Workspace view shown first when the dashboard loads.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Container width (px, 0 = inherit)', 'havenlytics' ) }
						value={ containerWidth }
						min={ 0 }
						max={ 1600 }
						step={ 20 }
						onChange={ ( value ) => setAttributes( { containerWidth: value } ) }
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Minimum height (px)', 'havenlytics' ) }
						value={ minHeight }
						min={ 0 }
						max={ 1400 }
						step={ 20 }
						onChange={ ( value ) => setAttributes( { minHeight: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Display', 'havenlytics' ) } initialOpen={ false }>
					<ToggleControl
						label={ __( 'Show header', 'havenlytics' ) }
						checked={ !! showHeader }
						onChange={ ( value ) => setAttributes( { showHeader: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show sidebar', 'havenlytics' ) }
						checked={ !! showSidebar }
						onChange={ ( value ) => setAttributes( { showSidebar: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show welcome card', 'havenlytics' ) }
						checked={ !! showWelcome }
						onChange={ ( value ) => setAttributes( { showWelcome: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show statistics', 'havenlytics' ) }
						checked={ !! showStatistics }
						onChange={ ( value ) => setAttributes( { showStatistics: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show quick actions', 'havenlytics' ) }
						checked={ !! showQuickActions }
						onChange={ ( value ) => setAttributes( { showQuickActions: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show recent properties', 'havenlytics' ) }
						checked={ !! showRecentProperties }
						onChange={ ( value ) => setAttributes( { showRecentProperties: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show favorites', 'havenlytics' ) }
						checked={ !! showFavorites }
						onChange={ ( value ) => setAttributes( { showFavorites: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show activity', 'havenlytics' ) }
						checked={ !! showActivity }
						onChange={ ( value ) => setAttributes( { showActivity: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show profile summary', 'havenlytics' ) }
						checked={ !! showProfileSummary }
						onChange={ ( value ) => setAttributes( { showProfileSummary: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Responsive', 'havenlytics' ) } initialOpen={ false }>
					<SelectControl
						label={ __( 'Sidebar', 'havenlytics' ) }
						value={ sidebarCollapse }
						options={ SIDEBAR_OPTIONS }
						onChange={ ( value ) => setAttributes( { sidebarCollapse: value } ) }
						help={ __( 'Initial sidebar state on wide screens. The app stays fully responsive on tablet and mobile.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Style', 'havenlytics' ) } initialOpen={ false }>
					<RangeControl
						label={ __( 'Card radius (px, 0 = inherit)', 'havenlytics' ) }
						value={ cardRadius }
						min={ 0 }
						max={ 32 }
						onChange={ ( value ) => setAttributes( { cardRadius: value } ) }
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Spacing (px, 0 = inherit)', 'havenlytics' ) }
						value={ spacing }
						min={ 0 }
						max={ 48 }
						onChange={ ( value ) => setAttributes( { spacing: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Shadow', 'havenlytics' ) }
						value={ shadow }
						options={ SHADOW_OPTIONS }
						onChange={ ( value ) => setAttributes( { shadow: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Container style', 'havenlytics' ) }
						value={ containerStyle }
						options={ CONTAINER_STYLE_OPTIONS }
						onChange={ ( value ) => setAttributes( { containerStyle: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Signed-out state', 'havenlytics' ) } initialOpen={ false }>
					<SelectControl
						label={ __( 'When signed out, show', 'havenlytics' ) }
						value={ loggedOutMode }
						options={ [
							{ label: __( 'Sign-in form', 'havenlytics' ), value: 'form' },
							{ label: __( 'Sign-in button', 'havenlytics' ), value: 'button' },
						] }
						onChange={ ( value ) => setAttributes( { loggedOutMode: value } ) }
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Heading', 'havenlytics' ) }
						value={ loggedOutHeading }
						onChange={ ( value ) => setAttributes( { loggedOutHeading: value } ) }
						placeholder={ __( 'Sign in to your dashboard', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<TextareaControl
						label={ __( 'Message', 'havenlytics' ) }
						value={ loggedOutMessage }
						onChange={ ( value ) => setAttributes( { loggedOutMessage: value } ) }
						placeholder={ __( 'You must sign in to access your dashboard.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					{ loggedOutMode === 'button' && (
						<>
							<TextControl
								label={ __( 'Button label', 'havenlytics' ) }
								value={ authButtonLabel }
								onChange={ ( value ) => setAttributes( { authButtonLabel: value } ) }
								placeholder={ __( 'Sign in', 'havenlytics' ) }
								__nextHasNoMarginBottom
							/>
							<TextControl
								label={ __( 'Button URL (blank = Workspace login)', 'havenlytics' ) }
								value={ authUrl }
								onChange={ ( value ) => setAttributes( { authUrl: value } ) }
								__nextHasNoMarginBottom
							/>
						</>
					) }
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<DashboardSkeleton attributes={ attributes } />
			</div>
		</>
	);
}
